import { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Group, GroupProps } from '@mantine/core';
import { IconArrowBigLeftLines, IconArrowBigRightLines } from '@tabler/icons-react';
import { useMutation } from '@apollo/client';
import { GET_LAST_STEP, GET_TRIP_PROFILE_STATE } from '@queries/trip-profiles';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';
import { TripDigestContext } from '@contexts/trip-digest-context';
import { QuestionState, QuestionStateInput } from '@generated/graphql';
import { SAVE_ALL_QUESTION_STATES } from '@queries/question-states';
import * as Sentry from '@sentry/react';

const BUTTON_COLOR = 'voyPurple.5';

enum SaveFunction {
  SaveQuestionnaire = 'saveQuestionnaire',
  // Add more save function names as needed
}
interface NextNavProps extends GroupProps {
  nextDisabled?: boolean;
  next?: string | null;
  saveDisabled?: boolean;
  saveVisible?: boolean;
  done?: string | null;
  saveFunctionName?: SaveFunction;
  saveFunction?: () => Promise<void>;
}

function NextNav({
  nextDisabled = false,
  next = null,
  saveDisabled = false,
  saveVisible = true,
  done = null,
  saveFunctionName = undefined,
  saveFunction = undefined,
  ...groupProps
}: NextNavProps) {
  const { questionStates } = useContext(TripDigestContext)!;
  const navigate = useNavigate();
  const { tripProfileId } = useParams();

  // Track which action is pending to show per-button loaders and prevent double-clicks
  type PendingAction = 'back' | 'save' | 'next' | 'done' | null;
  const [pending, setPending] = useState<PendingAction>(null);

  const [saveAllQuestionsState, { loading, error }] = useMutation(SAVE_ALL_QUESTION_STATES, {
    refetchQueries: [
      { query: GET_TRIP_PROFILE_STATE, variables: { tripProfileId } },
      { query: GET_LAST_STEP, variables: { id: tripProfileId } },
    ],
    awaitRefetchQueries: true,
    // errorPolicy: 'all',
  });

  if (loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;

  const goBack = () => {
    setPending('back');
    navigate(-1);
  };

  const saveQuestionnaire = async (navTo?: string): Promise<void> => {
    // console.log('>>>>>> next-nav questionStates before: ', questionStates);
    const questionsStates = [...questionStates.values()] as QuestionStateInput[];
    const results = (await saveAllQuestionsState({
      variables: {
        questionsState: questionsStates,
      },
    })) as { data: { saveAllQuestionsState: QuestionState[] } };
    if (results.data && results.data.saveAllQuestionsState && results.data.saveAllQuestionsState.length !== 6) {
      Sentry.captureException(Error('Did not persist 6 and only 6 to db!'), {
        extra: { values: JSON.stringify(results.data.saveAllQuestionsState, null, 2) },
      });
    }
    // console.log('>>>>>> next-nav questionStates after: ', questionStates);
    if (navTo) navigate(navTo);
  };

  // TODO: move save into the parent component, see the use of saveFunction()
  const handleAction = (navTo?: string, action?: Exclude<PendingAction, null>) => {
    try {
      // Set which button should show a loader
      setPending(action ?? (navTo ? 'next' : 'save'));

      switch (saveFunctionName) {
        case SaveFunction.SaveQuestionnaire:
          // TODO: Deprecate case SaveFunction.SaveQuestionnaire
          saveQuestionnaire(navTo)
            .catch((e) => {
              Sentry.captureException(e);
            })
            .finally(() => {
              // Clear loader if we didn't navigate away
              if (!navTo) setPending(null);
            });
          break;
        default:
          // Use provided saveFunction; navigate after it completes if navTo is provided
          if (saveFunction) {
            saveFunction()
              .then(() => {
                if (navTo) navigate(navTo);
              })
              .catch((e) => {
                console.error(e);
                Sentry.captureException(e);
              })
              .finally(() => {
                if (!navTo) setPending(null);
              });
          } else if (navTo) {
            navigate(navTo);
          } else {
            // No saveFunction and no navigation – just clear pending
            setPending(null);
          }
          break;
      }
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      setPending(null);
    }
  };

  const isBusy = pending !== null;

  return (
    <Group justify="right" pt={100} {...groupProps}>
      <Button variant="default" onClick={goBack} loading={pending === 'back'} disabled={isBusy && pending !== 'back'}>
        <IconArrowBigLeftLines />
        &nbsp;Back
      </Button>
      {saveVisible && (
        <Button
          variant="filled"
          color="green"
          disabled={saveDisabled || isBusy}
          loading={pending === 'save'}
          onClick={() => handleAction(undefined, 'save')}
        >
          Save
        </Button>
      )}
      {done && (
        <Button
          color={BUTTON_COLOR}
          variant="filled"
          disabled={isBusy}
          loading={pending === 'done'}
          onClick={() => handleAction(done, 'done')}
        >
          Done
        </Button>
      )}
      {next && (
        <Button
          color={BUTTON_COLOR}
          variant="filled"
          disabled={nextDisabled || isBusy}
          loading={pending === 'next'}
          onClick={() => handleAction(next, 'next')}
        >
          Next&nbsp;
          <IconArrowBigRightLines />
        </Button>
      )}
    </Group>
  );
}

export default NextNav;
export { SaveFunction };
